import React, { useMemo } from 'react';
import type { CampaignStats } from 'c-sdk';
import { AutoGrid } from 'c-components';
import { formatNumber, formatNumberWhole } from 'c-lib';
import HoursMins from 'c-main/Components/Campaign/CampaignReports/HoursMins';
import type { TypographyProps } from '@mui/material';
import { Box, Card, CardContent, Divider, Tooltip, Typography } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import type { TranslationPath } from 'c-translation';
import { Translate, useCommonTranslation } from 'c-translation';
import type { SettledTarget } from 'c-main/Lib';
import { resolveToDateHeadline } from './targetPacing';

type Props = {
    stats: CampaignStats;
    overallStats?: CampaignStats | null;
    showVariance: boolean;
    auditedStats?: CampaignStats | null;
    averageImpressions?: number | null;
    playsTitle?: TranslationPath;
    isGeopathOnly?: boolean;
    campaignValues: { show: boolean; delivered: number; expected: number; currencySign: string };
    campaignTarget?: number;
    fullCampaignTarget?: number | null;
    /** UL-8133 -- `vistar_us2` only: the headline as given; see `PCATotalsByMetric.tsx`. */
    targetHeadline?: SettledTarget | 'hidden' | null;
    isMediaOwner: boolean;
    isDsp?: boolean;
    isAverageAudienceMode?: boolean;
    isNonAuditedMode?: boolean;
    hideImpressionAnalysis?: boolean;
    nonAuditedImpressionsPercentage?: number | null;
    varianceContextLabel?: string;
    auditedVarianceLabel?: string | null;
    showAllAudiencesDeliveryOverview?: boolean;
    hasActiveFilters?: boolean;
    hasDateFilter?: boolean;
    overallSummaryDataDaysCount?: number;
    summaryDataDaysCount?: number;
    totalCampaignDays?: number;
    filteredEntityCount?: number | null;
    overallEntityCount?: number | null;
    filteredEntityLabel?: string | null;
};

const PCATotalsByMetric: React.FC<Props> = ({
    stats,
    overallStats = null,
    showVariance,
    campaignValues,
    isGeopathOnly,
    auditedStats,
    averageImpressions = null,
    playsTitle = 'Modules.Main.Campaigns.Overview.Analytics.playsTitle',
    campaignTarget,
    fullCampaignTarget = null,
    targetHeadline = null,
    isMediaOwner,
    isDsp = false,
    isAverageAudienceMode = false,
    isNonAuditedMode = false,
    hideImpressionAnalysis = false,
    nonAuditedImpressionsPercentage = null,
    varianceContextLabel,
    auditedVarianceLabel = null,
    showAllAudiencesDeliveryOverview = false,
    hasActiveFilters = false,
    hasDateFilter = false,
    overallSummaryDataDaysCount = 0,
    summaryDataDaysCount = 0,
    totalCampaignDays = 0,
    filteredEntityCount = null,
    overallEntityCount = null,
    filteredEntityLabel = null,
}) => {
    const t = useCommonTranslation();

    if (showVariance) {
        return (
            <AutoGrid spacing={4} xs={12} lg={campaignValues.show && !isMediaOwner ? 3 : 4}>
                <TotalsTile
                    title="Modules.Main.Campaigns.Overview.Analytics.impactsTitle"
                    actual={stats.impacts}
                    expected={stats.impacts_expected}
                    performance={+stats.impacts_percentage}
                    inSchedule={stats.impacts_in_schedule}
                    outSchedule={stats.impacts_out_schedule}
                    unbooked={stats.impacts_unbooked}
                    showVariance={showVariance}
                />
                <TotalsTile
                    title={playsTitle}
                    actual={stats.plays}
                    expected={stats.plays_expected}
                    performance={+stats.plays_percentage}
                    inSchedule={stats.plays_in_schedule}
                    outSchedule={stats.plays_out_schedule}
                    unbooked={stats.plays_unbooked}
                    showVariance={showVariance}
                />
                <TotalsTile
                    title="Modules.Main.Campaigns.Overview.Analytics.timeTitle"
                    asTime
                    actual={stats.time}
                    expected={stats.time_expected}
                    performance={+stats.time_percentage}
                    inSchedule={stats.time_in_schedule}
                    outSchedule={stats.time_out_schedule}
                    unbooked={stats.time_unbooked}
                    showVariance={showVariance}
                />

                {campaignValues?.show && !isMediaOwner && (
                    <CampaignValueCard
                        expected={campaignValues.expected}
                        delivered={campaignValues.delivered}
                        currencySign={campaignValues.currencySign}
                    />
                )}
            </AutoGrid>
        );
    }

    const totalImpressions = stats?.impacts ?? 0;
    const overallResolvedStats = overallStats ?? stats;
    const overallImpressions = overallResolvedStats?.impacts ?? totalImpressions;
    const totalPlays = stats?.plays ?? 0;
    const overallPlays = overallResolvedStats?.plays ?? totalPlays;
    // Averages mode mirrors audited mode: the average figure is primary and total DSP
    // impressions are the baseline, so variance = average - DSP.
    const comparisonPrimary = isAverageAudienceMode
        ? averageImpressions ?? 0
        : (auditedStats as any)?.geopath_audience ?? 0;
    const comparisonSecondary = isAverageAudienceMode
        ? totalImpressions
        : auditedStats?.impacts ?? 0;
    const comparisonPrimaryMultiplier = isAverageAudienceMode
        ? totalPlays > 0
            ? (averageImpressions ?? 0) / totalPlays
            : null
        : auditedStats?.plays
        ? ((auditedStats as any)?.geopath_audience ?? 0) / auditedStats.plays
        : null;
    const comparisonSecondaryDenominatorPlays = isAverageAudienceMode
        ? totalPlays
        : auditedStats?.plays ?? 0;
    const comparisonSecondaryMultiplier =
        comparisonSecondary != null && comparisonSecondaryDenominatorPlays > 0
            ? comparisonSecondary / comparisonSecondaryDenominatorPlays
            : null;
    const audienceVersionLabel =
        auditedVarianceLabel || t('Modules.Main.Campaigns.Overview.Analytics.audited');
    const auditedImpressionsPercentage =
        overallImpressions > 0 ? (comparisonSecondary / overallImpressions) * 100 : null;
    const dspVsAudienceVariance = comparisonPrimary - comparisonSecondary;
    const dspVsAudienceVariancePercentage =
        comparisonSecondary > 0 ? (comparisonPrimary / comparisonSecondary) * 100 : null;
    const dspVsAverageVariance =
        averageImpressions != null ? comparisonPrimary - comparisonSecondary : null;
    const dspVsAverageVariancePercentage =
        averageImpressions != null && comparisonSecondary > 0
            ? (comparisonPrimary / comparisonSecondary) * 100
            : null;

    const expectedImpressions = stats?.impacts_expected ?? 0;
    // What the whole campaign is expected to deliver, and what is left of it.
    // Both rows read from the same total so they cannot disagree, and remaining
    // is target minus delivered — positive while there is still delivery to come.
    const totalCampaignExpectedImpacts = fullCampaignTarget ?? expectedImpressions;
    const impactsRemaining = totalCampaignExpectedImpacts - totalImpressions;
    const geopathExpected = (stats as any)?.geopath_audience ?? null;

    const deliveryExpected = isGeopathOnly ? geopathExpected : expectedImpressions;

    // UL-8133 -- on vistar_us2 the headline target and % are passed in, never recomputed here.
    const toDateHeadline = targetHeadline
        ? resolveToDateHeadline({
              targetHeadline,
              campaignTarget,
              expectedImpressions,
              totalImpressions,
              hasActiveFilters,
          })
        : null;

    const deliveryPerformance = toDateHeadline
        ? toDateHeadline.performance
        : campaignTarget
        ? (totalImpressions / campaignTarget) * 100
        : deliveryExpected > 0
        ? (totalImpressions / deliveryExpected) * 100
        : null;

    const deliveryVariance = toDateHeadline
        ? toDateHeadline.expected != null
            ? totalImpressions - toDateHeadline.expected
            : null
        : campaignTarget
        ? totalImpressions - campaignTarget
        : deliveryExpected != null
        ? totalImpressions - deliveryExpected
        : null;
    const overallTargetImpressions = fullCampaignTarget ?? campaignTarget ?? expectedImpressions;
    // vistar_us2 with no target shows "-", never 0 (UL-8203).
    const toDateTargetImpressions = toDateHeadline
        ? toDateHeadline.expected
        : campaignTarget || expectedImpressions;
    const overallTargetAchieved =
        overallTargetImpressions > 0 ? (overallImpressions / overallTargetImpressions) * 100 : null;
    const toDateTargetAchieved = toDateHeadline
        ? toDateHeadline.performance
        : toDateTargetImpressions != null && toDateTargetImpressions > 0
        ? (totalImpressions / toDateTargetImpressions) * 100
        : null;
    const overallDeliveryVariance =
        overallTargetImpressions != null ? overallImpressions - overallTargetImpressions : null;
    const toDateDeliveryVariance =
        toDateTargetImpressions == null ? null : totalImpressions - toDateTargetImpressions;
    const toDateShowsShare = toDateHeadline
        ? toDateHeadline.mode === 'shareOfTotal'
        : hasActiveFilters;
    const filteredTotalImpressionsPercentage =
        overallImpressions > 0 ? (totalImpressions / overallImpressions) * 100 : null;
    const unfilteredImpressionsDifference = overallImpressions - totalImpressions;
    const cardSx = {
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
    } as const;

    const cardContentSx = {
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
    } as const;
    const resolvedVarianceContextLabel = isAverageAudienceMode
        ? varianceContextLabel
        : auditedVarianceLabel || varianceContextLabel;
    const totalImpactsTitle = t(
        isDsp
            ? 'Modules.Main.Campaigns.Overview.Analytics.totalDeliveredImpacts'
            : 'Modules.Main.Campaigns.Overview.Analytics.totalImpacts',
    );
    const variancePercTitleText = resolvedVarianceContextLabel
        ? t('Modules.Main.Campaigns.Overview.Analytics.variancePercWithContext', {
              value: resolvedVarianceContextLabel,
          })
        : t('Modules.Main.Campaigns.Overview.Analytics.variancePerc');
    const varianceTitleText = resolvedVarianceContextLabel
        ? t('Modules.Main.Campaigns.Overview.Analytics.varianceWithContext', {
              value: resolvedVarianceContextLabel,
          })
        : t('Modules.Main.Campaigns.Overview.Analytics.variance');
    const deliveryTitle = isGeopathOnly
        ? t('Modules.Main.Campaigns.Overview.Analytics.deliverySummary')
        : t('Modules.Main.Campaigns.Overview.Analytics.deliveryOfTarget');
    const renderDeliveryOverviewCard = (
        titleText: React.ReactNode,
        performanceTitleText: React.ReactNode,
        deliveredTitleText: React.ReactNode,
        targetTitleText: React.ReactNode,
        varianceTitleText: React.ReactNode,
        targetValue: number | null,
        performanceValue: number | null,
        deliveredValue: number | null,
        varianceValue: number | null,
        campaignDaysValueText: string | null,
        showVariancePlusMinus = true,
        isDimmed = false,
        showSpacerRow = false,
        secondaryFooterTitleText: React.ReactNode = null,
        secondaryFooterValue: number | null = null,
        footerTitleText: React.ReactNode = t(
            'Modules.Main.Campaigns.Overview.Analytics.campaignDays',
        ),
    ) => (
        <Card
            sx={{
                ...cardSx,
                bgcolor: isDimmed ? 'action.hover' : undefined,
            }}
        >
            <CardContent sx={cardContentSx}>
                <Typography variant="h4" color="textPrimary">
                    {titleText}
                </Typography>
                <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                <AutoGrid spacing={0} xs={12}>
                    <FormatCount
                        asTime={false}
                        count={performanceValue}
                        countSuffix={performanceValue != null ? '%' : ''}
                        asWholeNumber={false}
                        titleText={performanceTitleText}
                        countVariant="h4"
                    />
                    <FormatCount
                        asTime={false}
                        count={deliveredValue}
                        titleText={deliveredTitleText}
                    />
                    <FormatCount asTime={false} count={targetValue} titleText={targetTitleText} />
                    <FormatCount
                        asTime={false}
                        count={varianceValue}
                        asWholeNumber
                        showPlusMinus={showVariancePlusMinus}
                        titleText={varianceTitleText}
                    />

                    {showSpacerRow && <Box minHeight={26.5} />}

                    <Divider sx={{ borderColor: 'grey.300', mt: 1, mb: 1 }} />

                    {secondaryFooterTitleText && (
                        <FormatCount
                            asTime={false}
                            count={secondaryFooterValue}
                            titleText={secondaryFooterTitleText}
                        />
                    )}

                    <AutoGrid spacing={1} justifyContent="space-between">
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" color="textSecondary">
                                {footerTitleText}
                            </Typography>
                        </Box>
                        <Box display="flex" alignItems="center">
                            <Typography variant="subtitle1" color="textPrimary">
                                {campaignDaysValueText ?? '-'}
                            </Typography>
                        </Box>
                    </AutoGrid>
                </AutoGrid>
            </CardContent>
        </Card>
    );
    const renderDspMultiplierTitle = (tooltipText: string) => (
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
            {t('Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplier')}
            <Tooltip title={tooltipText} arrow>
                <InfoOutlinedIcon sx={{ fontSize: 14, cursor: 'help' }} />
            </Tooltip>
        </Box>
    );
    const renderFilterImpactsLabel = (filtered: boolean) => (
        <Box display="flex" alignItems="center" gap={0.5}>
            <Typography component="span" variant="inherit">
                {t('CommonWords.Impacts')}
            </Typography>
            {filtered ? (
                <FilterAltIcon sx={{ fontSize: 16 }} />
            ) : (
                <FilterAltOffIcon sx={{ fontSize: 16 }} />
            )}
        </Box>
    );
    const overallCampaignDaysText =
        totalCampaignDays > 0
            ? `${overallSummaryDataDaysCount || summaryDataDaysCount} of ${totalCampaignDays}`
            : null;
    const toDateCampaignDaysText =
        totalCampaignDays > 0 ? `${summaryDataDaysCount} of ${totalCampaignDays}` : null;
    const toDateDaysLabel = hasDateFilter
        ? t('Modules.Main.Campaigns.Overview.Analytics.campaignDays')
        : t('Modules.Main.Campaigns.Overview.Analytics.numberOfDaysLive');
    if (showAllAudiencesDeliveryOverview) {
        return (
            <AutoGrid spacing={4} xs={12} lg={6} alignItems="stretch">
                {renderDeliveryOverviewCard(
                    hasActiveFilters
                        ? t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall')
                        : t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall'),
                    t(
                        'Modules.Main.Campaigns.Overview.Analytics.percentageOfTotalTargetImpressions',
                    ),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveredImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.totalTargetImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveryVariance'),
                    overallTargetImpressions,
                    overallTargetAchieved,
                    overallImpressions,
                    overallDeliveryVariance,
                    overallCampaignDaysText,
                    true,
                    hasActiveFilters,
                    false,
                    filteredEntityLabel,
                    overallEntityCount,
                )}
                {toDateShowsShare
                    ? renderDeliveryOverviewCard(
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewToDate'),
                          t(
                              'Modules.Main.Campaigns.Overview.Analytics.percentageOfTotalImpressions',
                          ),
                          renderFilterImpactsLabel(true),
                          t('Modules.Main.Campaigns.Overview.Analytics.totalDeliveredImpressions'),
                          renderFilterImpactsLabel(false),
                          overallImpressions,
                          filteredTotalImpressionsPercentage,
                          totalImpressions,
                          unfilteredImpressionsDifference,
                          toDateCampaignDaysText,
                          false,
                          false,
                          false,
                          filteredEntityLabel,
                          filteredEntityCount,
                          toDateDaysLabel,
                      )
                    : renderDeliveryOverviewCard(
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewToDate'),
                          t(
                              'Modules.Main.Campaigns.Overview.Analytics.percentageOfTargetImpressions',
                          ),
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveredImpressions'),
                          t('Modules.Main.Campaigns.Overview.Analytics.expectedImpressionsToDate'),
                          t('Modules.Main.Campaigns.Overview.Analytics.deliveryVariance'),
                          toDateTargetImpressions,
                          toDateTargetAchieved,
                          totalImpressions,
                          toDateDeliveryVariance,
                          toDateCampaignDaysText,
                          true,
                          false,
                          false,
                          filteredEntityLabel,
                          filteredEntityCount,
                          toDateDaysLabel,
                      )}
            </AutoGrid>
        );
    }

    return (
        <AutoGrid spacing={4} xs={12} lg={hideImpressionAnalysis ? 12 : 6} alignItems="stretch">
            {isGeopathOnly || isAverageAudienceMode || isNonAuditedMode ? (
                renderDeliveryOverviewCard(
                    isNonAuditedMode
                        ? t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall')
                        : t('Modules.Main.Campaigns.Overview.Analytics.deliveryOverviewOverall'),
                    t(
                        'Modules.Main.Campaigns.Overview.Analytics.percentageOfTotalTargetImpressions',
                    ),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveredImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.totalTargetImpressions'),
                    t('Modules.Main.Campaigns.Overview.Analytics.deliveryVariance'),
                    overallTargetImpressions,
                    overallTargetAchieved,
                    overallImpressions,
                    overallDeliveryVariance,
                    overallCampaignDaysText,
                    true,
                    isAverageAudienceMode || isNonAuditedMode ? hasActiveFilters : true,
                    true,
                    t('Modules.Main.Campaigns.Overview.Analytics.totalPlays'),
                    overallPlays,
                )
            ) : (
                <Card sx={cardSx}>
                    <CardContent sx={cardContentSx}>
                        <Typography variant="h4" color="textPrimary">
                            {deliveryTitle}
                        </Typography>
                        <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                        <AutoGrid spacing={0} xs={12}>
                            <FormatCount
                                asTime={false}
                                count={deliveryPerformance}
                                countSuffix={deliveryPerformance != null ? '%' : ''}
                                asWholeNumber={false}
                                title="Modules.Main.Campaigns.Overview.Analytics.impactsPerformance"
                                countVariant="h4"
                            />

                            <FormatCount
                                asTime={false}
                                count={totalImpressions}
                                titleText={totalImpactsTitle}
                            />

                            <FormatCount
                                asTime={false}
                                count={toDateTargetImpressions}
                                title="Modules.Main.Campaigns.Overview.Analytics.expectedImpactsToDate"
                            />

                            <FormatCount
                                asTime={false}
                                count={deliveryVariance}
                                showPlusMinus
                                asWholeNumber
                                title="Modules.Main.Campaigns.Overview.Analytics.variance"
                            />

                            <Divider sx={{ borderColor: 'grey.300', my: 1 }} />

                            <FormatCount
                                asTime={false}
                                count={totalCampaignExpectedImpacts}
                                title="Modules.Main.Campaigns.Overview.Analytics.totalCampaignExpectedImpacts"
                            />

                            <FormatCount
                                asTime={false}
                                count={impactsRemaining}
                                showPlusMinus
                                title="Modules.Main.Campaigns.Overview.Analytics.impactsRemaining"
                            />

                            {hideImpressionAnalysis && nonAuditedImpressionsPercentage != null && (
                                <FormatCount
                                    asTime={false}
                                    asWholeNumber={false}
                                    count={nonAuditedImpressionsPercentage}
                                    countSuffix="%"
                                    titleText={t(
                                        'Modules.Main.Campaigns.Overview.Analytics.nonAuditedImpactsPercentage',
                                    )}
                                />
                            )}
                        </AutoGrid>
                    </CardContent>
                </Card>
            )}

            {!hideImpressionAnalysis && (
                <Card sx={cardSx}>
                    <CardContent sx={cardContentSx}>
                        <Typography variant="h4" color="textPrimary">
                            {isAverageAudienceMode ? (
                                <Translate path="Modules.Main.Campaigns.Overview.Analytics.averageFormatDeliveryOverview" />
                            ) : isNonAuditedMode ? (
                                <Translate path="Modules.Main.Campaigns.Overview.Analytics.nonAuditedDeliveryOverview" />
                            ) : (
                                t(
                                    'Modules.Main.Campaigns.Overview.Analytics.audienceDeliveryOverview',
                                    {
                                        value: audienceVersionLabel,
                                    },
                                )
                            )}
                        </Typography>
                        <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                        <AutoGrid spacing={0} xs={12}>
                            {isNonAuditedMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={nonAuditedImpressionsPercentage}
                                        countSuffix={
                                            nonAuditedImpressionsPercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.percentageOfCampaignImpressionsNonAudited"
                                        countVariant="h4"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={totalImpressions}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredNonAuditedImpressions"
                                    />

                                    {/* Blank lines to line up with the delivery overview card */}
                                    <Box minHeight={26.5} />
                                    <Box minHeight={26.5} />
                                    <Box minHeight={26.5} />
                                </>
                            ) : isAverageAudienceMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAverageVariancePercentage}
                                        countSuffix={
                                            dspVsAverageVariancePercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.dspVsAverageVariancePerc"
                                        countVariant="h4"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonSecondary}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredDspImpressions"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimary}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredAverageImpressions"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAverageVariance}
                                        showPlusMinus
                                        title="Modules.Main.Campaigns.Overview.Analytics.dspVsAverageVariance"
                                    />

                                    {/* Blank line to line up with the delivery overview card's spacer row */}
                                    <Box minHeight={26.5} />
                                </>
                            ) : (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={auditedImpressionsPercentage}
                                        countSuffix={
                                            auditedImpressionsPercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.percentageOfCampaignImpressionsAudited"
                                        countVariant="h4"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAudienceVariancePercentage}
                                        countSuffix={
                                            dspVsAudienceVariancePercentage != null ? '%' : ''
                                        }
                                        asWholeNumber={false}
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.dspVsAudienceVariancePerc',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonSecondary}
                                        title="Modules.Main.Campaigns.Overview.Analytics.deliveredDspImpressions"
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimary}
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.deliveredAudienceImpressions',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />

                                    <FormatCount
                                        asTime={false}
                                        count={dspVsAudienceVariance}
                                        showPlusMinus
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.dspVsAudienceVariance',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />
                                </>
                            )}

                            <Divider sx={{ borderColor: 'grey.300', my: 1 }} />

                            {isNonAuditedMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={
                                            overallPlays > 0
                                                ? overallImpressions / overallPlays
                                                : null
                                        }
                                        asWholeNumber={false}
                                        titleText={renderDspMultiplierTitle(
                                            t(
                                                'Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplierInfoAll',
                                            ),
                                        )}
                                    />
                                    <FormatCount
                                        asTime={false}
                                        count={
                                            totalPlays > 0 ? totalImpressions / totalPlays : null
                                        }
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.averageNonAuditedImpressionMultiplier"
                                    />
                                </>
                            ) : isAverageAudienceMode ? (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={
                                            overallPlays > 0
                                                ? overallImpressions / overallPlays
                                                : null
                                        }
                                        asWholeNumber={false}
                                        titleText={renderDspMultiplierTitle(
                                            t(
                                                'Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplierInfoAll',
                                            ),
                                        )}
                                    />
                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimaryMultiplier}
                                        asWholeNumber={false}
                                        title="Modules.Main.Campaigns.Overview.Analytics.averageImpactsMultiplier"
                                    />
                                </>
                            ) : (
                                <>
                                    <FormatCount
                                        asTime={false}
                                        count={comparisonSecondaryMultiplier}
                                        asWholeNumber={false}
                                        titleText={renderDspMultiplierTitle(
                                            t(
                                                'Modules.Main.Campaigns.Overview.Analytics.averageDspImpressionMultiplierInfoAudited',
                                            ),
                                        )}
                                    />
                                    <FormatCount
                                        asTime={false}
                                        count={comparisonPrimaryMultiplier}
                                        asWholeNumber={false}
                                        titleText={t(
                                            'Modules.Main.Campaigns.Overview.Analytics.averageAudienceImpressionMultiplier',
                                            {
                                                value: audienceVersionLabel,
                                            },
                                        )}
                                    />
                                </>
                            )}
                        </AutoGrid>
                    </CardContent>
                </Card>
            )}
        </AutoGrid>
    );
};

type TileProps = {
    title: TranslationPath;
    actual: number;
    expected: number;
    performance: number;

    inSchedule: number;
    outSchedule: number;
    unbooked: number;
    asTime?: boolean;
    showVariance?: boolean;
};

const TotalsTile: React.FC<TileProps> = ({
    title,
    actual,
    expected,
    performance,
    asTime,
    inSchedule,
    outSchedule,
    unbooked,
    showVariance,
}) => {
    const performanceColour = useMemo(() => {
        if (expected <= 0) return undefined;
        return performance >= 100 ? 'success.main' : 'warning.main';
    }, [expected, performance]);
    const variance = useMemo(() => actual - expected, [actual, expected]);
    const varianceColour = useMemo(() => {
        if (actual - expected > 0) return 'success.main';
        if (actual - expected === 0) return null;
        return 'warning.main';
    }, [actual, expected]);

    return (
        <Card>
            <CardContent>
                <Typography variant="h4" color="textPrimary">
                    <Translate path={title} />
                </Typography>
                <Divider sx={{ borderBottomWidth: 2, my: 1 }} />
                <AutoGrid spacing={0} xs={12}>
                    <FormatCount
                        asTime={false}
                        count={expected > 0 ? performance : null}
                        countSuffix={expected > 0 ? '%' : ''}
                        title="Modules.Main.Campaigns.Overview.Analytics.performance"
                        countColor={performanceColour}
                        countVariant="h4"
                        asWholeNumber={false}
                    />
                    <FormatCount
                        count={expected}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.expected"
                    />
                    <FormatCount
                        count={actual}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.actual"
                    />
                    {showVariance && (
                        <FormatCount
                            count={variance}
                            asTime={asTime === true}
                            showPlusMinus
                            countColor={varianceColour}
                            title="Modules.Main.Campaigns.Overview.Analytics.variance"
                        />
                    )}
                    <FormatCount
                        count={inSchedule}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.inSchedule"
                        titleVariant="body2"
                        countVariant="body2"
                    />
                    <FormatCount
                        count={outSchedule}
                        asTime={asTime === true}
                        title="Modules.Main.Campaigns.Overview.Analytics.outSchedule"
                        titleVariant="body2"
                        countVariant="body2"
                    />
                    <FormatCount
                        asTime={asTime === true}
                        count={unbooked}
                        title="Modules.Main.Campaigns.Overview.Analytics.unbooked"
                        titleVariant="body2"
                        countVariant="body2"
                    />
                </AutoGrid>
            </CardContent>
        </Card>
    );
};

const CampaignValueCard: React.FC<{
    expected: number;
    delivered: number;
    currencySign: string;
}> = ({ expected, delivered, currencySign }) => {
    const currency = (n: number) => `${currencySign}${formatNumberWhole(Math.abs(n))}`;

    return (
        <Card sx={{ height: '100%' }}>
            <CardContent>
                <Typography variant="h4" color="textPrimary">
                    <Translate path="Modules.Main.Campaigns.Overview.Analytics.valueTitle" />
                </Typography>
                <Divider sx={{ borderBottomWidth: 2, my: 1 }} />

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle1" color="textSecondary">
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.expectedValue" />
                    </Typography>
                    <Typography variant="subtitle1" color="textSecondary" sx={{ fontWeight: 600 }}>
                        {currency(expected)}
                    </Typography>
                </Box>

                <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                    <Typography variant="subtitle1" color="textSecondary">
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.actualValueDelivered" />
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color={
                            delivered > expected
                                ? 'success.main'
                                : expected > delivered
                                ? 'warning.main'
                                : null
                        }
                        sx={{ fontWeight: 600 }}
                    >
                        {currency(delivered)}
                    </Typography>
                </Box>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                    <Typography variant="subtitle1" color="textSecondary">
                        <Translate path="Modules.Main.Campaigns.Overview.Analytics.variance" />
                    </Typography>
                    <Typography
                        variant="subtitle1"
                        color={
                            delivered > expected
                                ? 'success.main'
                                : expected > delivered
                                ? 'warning.main'
                                : null
                        }
                        sx={{ fontWeight: 600 }}
                    >
                        {delivered < expected
                            ? `-${currency(delivered - expected)}`
                            : currency(delivered - expected)}
                    </Typography>
                </Box>
            </CardContent>
        </Card>
    );
};

const FormatCount = React.memo<{
    count?: number | null;
    countSuffix?: React.ReactNode;
    asTime: boolean;
    asWholeNumber?: boolean;
    title?: TranslationPath;
    titleText?: React.ReactNode;
    titleColor?: TypographyProps['color'];
    titleVariant?: TypographyProps['variant'];
    showPlusMinus?: boolean;
    countColor?: TypographyProps['color'];
    countVariant?: TypographyProps['variant'];
}>(function FormatCount({
    count,
    countSuffix,
    asTime,
    asWholeNumber = true,
    title,
    titleText,
    showPlusMinus = false,
    titleColor = 'textSecondary',
    titleVariant = 'subtitle1',
    countColor = 'textPrimary',
    countVariant = 'subtitle1',
}) {
    const hasValue = count !== null && count !== undefined;
    const sign =
        showPlusMinus && hasValue
            ? (count as number) > 0
                ? '+'
                : (count as number) < 0
                ? '-'
                : ''
            : '';

    const absCount = hasValue ? Math.abs(count as number) : undefined;

    return (
        <AutoGrid spacing={1} justifyContent="space-between">
            <Box display="flex" alignItems="center">
                <Typography variant={titleVariant} color={titleColor}>
                    {titleText ?? (title ? <Translate path={title} /> : null)}
                </Typography>
            </Box>

            {asTime ? (
                <Box display="flex" alignItems="center">
                    {hasValue ? (
                        <>
                            {showPlusMinus && sign && (
                                <Typography
                                    variant={countVariant}
                                    color={countColor}
                                    sx={{ mr: 0.25 }}
                                >
                                    {sign}
                                </Typography>
                            )}
                            <HoursMins
                                color={countColor}
                                seconds={absCount}
                                variant={countVariant}
                            />
                        </>
                    ) : (
                        <Typography variant={countVariant} color={countColor}>
                            -
                        </Typography>
                    )}
                </Box>
            ) : (
                <Box display="flex" alignItems="center">
                    <Typography variant={countVariant} color={countColor}>
                        {hasValue
                            ? `${sign}${
                                  asWholeNumber
                                      ? formatNumberWhole(absCount as number)
                                      : formatNumber(absCount as number)
                              }${countSuffix ?? ''}`
                            : '-'}
                    </Typography>
                </Box>
            )}
        </AutoGrid>
    );
});

export default PCATotalsByMetric;
